/**
 * useImageGroups — 图片分组管理 Hook
 *
 * 策略：localStorage 乐观更新（立即生效）+ 后台同步 API。
 *   - 接口未通时：完全走 localStorage，行为与之前一致。
 *   - 接口通了后：mount 时从服务端加载最新数据，每次操作同步调 API。
 *
 * PC 版（MyImages）和 M 版共用此 hook，
 * 后端接口调通后两端自动生效，无需分别修改。
 *
 * 后端接口约定：
 *   GET    /api/video/base_vid/image/groups
 *   POST   /api/video/base_vid/image/groups              body: { name }
 *   PUT    /api/video/base_vid/image/groups/:id          body: { name }
 *   DELETE /api/video/base_vid/image/groups/:id
 *   POST   /api/video/base_vid/image/groups/:id/assign   body: { imageIds }
 */
import { useState, useCallback, useEffect, useRef } from 'react';
import apiService from '../services/api';

const storageKey = (phone) => `talkinghead_imageGroups_${phone || 'guest'}`;

const loadFromStorage = (phone) => {
  try {
    const raw = localStorage.getItem(storageKey(phone));
    return raw ? JSON.parse(raw) : { groups: [], assignments: {} };
  } catch {
    return { groups: [], assignments: {} };
  }
};

const saveToStorage = (phone, data) => {
  try {
    localStorage.setItem(storageKey(phone), JSON.stringify(data));
  } catch { /* ignore */ }
};

export const useImageGroups = (userPhone) => {
  const [data, setData] = useState(() => loadFromStorage(userPhone));
  const phoneRef = useRef(userPhone);

  useEffect(() => {
    phoneRef.current = userPhone;
    // 先用本地数据，确保立即可用
    const local = loadFromStorage(userPhone);
    setData(local);
    // 尝试从后端拉取最新分组，接口通了后自动合并
    apiService.getImageGroups()
      .then((res) => {
        if (res?.code === 0 && res.data) {
          const groups = Array.isArray(res.data.groups) ? res.data.groups : [];
          const assignments = res.data.assignments ?? {};
          const merged = { groups, assignments };
          setData(merged);
          saveToStorage(userPhone, merged);
        }
      })
      .catch(() => { /* 接口未通，静默忽略，继续用 localStorage */ });
  }, [userPhone]);

  // 本地立即更新 + 持久化到 localStorage
  const update = useCallback((updater) => {
    setData((prev) => {
      const next = updater(prev);
      saveToStorage(phoneRef.current, next);
      return next;
    });
  }, []);

  /** 新建分组，返回后端返回的真实 groupId */
  const createGroup = useCallback(async (name) => {
    try {
      const res = await apiService.createImageGroup(name);
      if (res?.code === 0 && res?.data?.id) {
        // Use the real ID from backend response
        const realId = String(res.data.id);
        update((prev) => ({
          ...prev,
          groups: [...prev.groups, { id: realId, name: res.data.group_name || name }],
        }));
        return realId;
      } else {
        throw new Error(res?.msg || '创建分组失败');
      }
    } catch (error) {
      console.error('Failed to create image group:', error);
      throw error;
    }
  }, []);

  /** 重命名分组 */
  const renameGroup = useCallback((groupId, newName) => {
    update((prev) => ({
      ...prev,
      groups: prev.groups.map((g) => g.id === groupId ? { ...g, name: newName } : g),
    }));
    apiService.renameImageGroup(groupId, newName).catch(() => {});
  }, [update]);

  /** 删除分组，该组内图片归回"未分组" */
  const deleteGroup = useCallback((groupId) => {
    update((prev) => ({
      groups: prev.groups.filter((g) => g.id !== groupId),
      assignments: Object.fromEntries(
        Object.entries(prev.assignments).filter(([, gid]) => gid !== groupId)
      ),
    }));
    apiService.deleteImageGroup(groupId).catch(() => {});
  }, [update]);

  /** 批量分配图片到分组，groupId 为 null 时移回"未分组" */
  const assignImages = useCallback(async (imageIds, groupId) => {
    // Store previous assignments for rollback
    let previousAssignments = null;
    setData((prev) => {
      previousAssignments = { ...prev.assignments };
      const next = { ...prev.assignments };
      imageIds.forEach((id) => {
        const key = String(id);
        if (!groupId) delete next[key];
        else next[key] = groupId;
      });
      const updated = { ...prev, assignments: next };
      saveToStorage(phoneRef.current, updated);
      return updated;
    });

    if (groupId) {
      try {
        const res = await apiService.assignImagesToGroup(groupId, imageIds);
        if (res?.code !== 0) {
          throw new Error(res?.msg || 'Assignment failed');
        }
      } catch (error) {
        console.error('Failed to assign images to group:', error);
        // Rollback to previous state on failure
        if (previousAssignments !== null) {
          setData((prev) => {
            const rolledBack = { ...prev, assignments: previousAssignments };
            saveToStorage(phoneRef.current, rolledBack);
            return rolledBack;
          });
        }
        throw error; // Re-throw so caller can handle it
      }
    }
  }, []);

  return {
    groups: data.groups,
    assignments: data.assignments,
    createGroup,
    renameGroup,
    deleteGroup,
    assignImages,
  };
};
